import { Box, Grid } from '@material-ui/core';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutomobileFormSection } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/AutomobileFormSection';
import { FieldHint } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/FieldHint';
import { PreviousValueHint } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/PreviousValueHint';
import { VehicleConditionChips } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/VehicleConditionChips';
import { RegistrationDateMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/maps/registrationDate.map';
import { setIsIdentificationNumberTaken } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/schemas/identificationNumberTakenState';
import {
  AutomobileFormFieldName,
  AutomobilePreviousValues,
} from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormFieldName';
import { useCheckReference } from '@/components/modules/Application/components/References/_shared/hooks/useCheckReference';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { SelectOption } from '@/types/selectOption';

export interface AutomobileFormFieldsProps {
  requireIdentificationNumber?: boolean;
  shouldCheckIdentificationReference?: boolean;
  isFieldDisabled?: (fieldName: AutomobileFormFieldName) => boolean;
  alwaysShowFieldsWhenNewVehicle?: boolean;
  highlightEditableFields?: boolean;
  previousValues?: AutomobilePreviousValues;
  dense?: boolean;
}

const withCurrentValueOption = (options: SelectOption[], currentValue?: string): SelectOption[] =>
  currentValue && !options.some((option) => option.value === currentValue)
    ? [...options, { value: currentValue, display: currentValue }]
    : options;

export const AutomobileFormFields = ({
  requireIdentificationNumber,
  shouldCheckIdentificationReference = true,
  isFieldDisabled,
  alwaysShowFieldsWhenNewVehicle,
  highlightEditableFields = false,
  previousValues,
  dense = false,
}: AutomobileFormFieldsProps) => {
  const {
    state: { automobileData },
  } = useApplicationContext();
  const { watch, setValue, trigger } = useFormContext();
  const {
    state: { language },
  } = useConfigContext();
  const { referenceAvailable, isReferenceTaken } = useCheckReference({
    referenceName: 'item.identificationNumber',
    enabled: shouldCheckIdentificationReference,
  });

  const isNewVehicle = watch('item.isNewVehicle');
  const brand = watch('item.brand');
  const model = watch('item.model');
  const version = watch('item.version');
  const versionCode = watch('item.versionCode');
  const registrationDate = watch('item.registrationDate');
  const isNewVehicleFieldDisabled = isFieldDisabled?.('isNewVehicle') ?? false;
  const isModelFieldDisabled = isFieldDisabled?.('model') ?? false;
  const isVersionFieldDisabled = isFieldDisabled?.('version') ?? false;

  const brandOptionList = useMemo(
    () =>
      withCurrentValueOption(
        automobileData?.brands.map((brand) => ({ value: brand.value, display: brand.display })) ?? [],
        brand,
      ),
    [automobileData?.brands, brand],
  );

  const modelOptionList = useMemo(
    () =>
      withCurrentValueOption(
        automobileData?.brandModels.map((brand) => ({ value: brand.value, display: brand.display })) ?? [],
        model,
      ),
    [automobileData?.brandModels, model],
  );

  const versionOptionList = useMemo(
    () =>
      withCurrentValueOption(
        automobileData?.versions.map((brand) => ({ value: brand.value, display: brand.display })) ?? [],
        version,
      ),
    [automobileData?.versions, version],
  );

  useEffect(() => {
    if (!alwaysShowFieldsWhenNewVehicle && !isNewVehicleFieldDisabled && isNewVehicle) {
      setValue('item.registrationDate', null);
      setValue('item.odometer', null);
    }
  }, [isNewVehicle, isNewVehicleFieldDisabled, alwaysShowFieldsWhenNewVehicle]);

  useEffect(() => {
    if (!brand && brandOptionList.length === 1) {
      setValue('item.brand', brandOptionList[0].value);
    }
  }, [brandOptionList]);

  useEffect(() => {
    setIsIdentificationNumberTaken(isReferenceTaken);
    trigger('item.identificationNumber');
  }, [isReferenceTaken, trigger]);

  useEffect(() => {
    return () => {
      setIsIdentificationNumberTaken(false);
    };
  }, []);

  const showUsageFields = !isNewVehicle || alwaysShowFieldsWhenNewVehicle;
  const registrationDateHint = RegistrationDateMap.toRelativeText(registrationDate, language);

  const plateNumberField = (
    <TextFormField
      name={'item.plateNumber'}
      label={'LICENSE_PLATE_NUMBER'}
      fieldVariant={FieldVariant.form}
      tooltipTitle={!showUsageFields ? <Text tId="NEW_VEHICLE_PLATE_NUMBER_HINT" /> : undefined}
      markAsOptional
      disabled={isFieldDisabled?.('plateNumber')}
      highlightLabel={highlightEditableFields && !isFieldDisabled?.('plateNumber')}
      onChangeCallback={(event) => {
        const upperCaseValue = event.target.value.toUpperCase();

        if (event.target.value !== upperCaseValue) {
          setValue('item.plateNumber', upperCaseValue, { shouldDirty: true, shouldValidate: true });
        }
      }}
    />
  );

  const identificationNumberField = (
    <TextFormField
      name={'item.identificationNumber'}
      label={'IDENTIFICATION_NUMBER'}
      fieldVariant={FieldVariant.form}
      tooltipTitle={!showUsageFields ? <Text tId="NEW_VEHICLE_IDENTIFICATION_NUMBER_HINT" /> : undefined}
      markAsOptional={!requireIdentificationNumber}
      disabled={isFieldDisabled?.('identificationNumber')}
      highlightLabel={highlightEditableFields && !isFieldDisabled?.('identificationNumber')}
      InputProps={{
        ...(requireIdentificationNumber && {
          endAdornment: referenceAvailable,
        }),
      }}
      onChangeCallback={(event) => {
        const upperCaseValue = event.target.value.toUpperCase();

        if (event.target.value !== upperCaseValue) {
          setValue('item.identificationNumber', upperCaseValue, { shouldDirty: true, shouldValidate: true });
        }
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" gridGap={dense ? 24 : 32}>
      <VehicleConditionChips
        disabled={isNewVehicleFieldDisabled}
        selectedTextId={isNewVehicle ? 'NEW_VEHICLE_CONDITION' : 'USED_VEHICLE_CONDITION'}
      />
      <AutomobileFormSection title="VEHICLE_MODEL_SECTION">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutocompleteSelectFormField
              name={'item.brand'}
              label={'BRAND'}
              selectOptions={brandOptionList}
              fieldVariant={FieldVariant.form}
              disabled={isFieldDisabled?.('brand')}
              highlightLabel={highlightEditableFields && !isFieldDisabled?.('brand')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteSelectFormField
              name={'item.model'}
              label={'MODEL'}
              tooltipTitle={<Text tId="MODEL_DATE_SUFFIX_TOOLTIP" />}
              selectOptions={modelOptionList}
              disabled={isModelFieldDisabled ? true : !automobileData?.brandModels.length}
              fieldVariant={FieldVariant.form}
              highlightLabel={highlightEditableFields && !isModelFieldDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <AutocompleteSelectFormField
              name={'item.version'}
              label={'version'}
              tooltipTitle={<Text tId="VERSION_DATE_SUFFIX_TOOLTIP" />}
              selectOptions={versionOptionList}
              disabled={isVersionFieldDisabled ? true : !automobileData?.versions.length}
              fieldVariant={FieldVariant.form}
              highlightLabel={highlightEditableFields && !isVersionFieldDisabled}
            />
            {versionCode && (
              <FieldHint>
                <Text tId="EUROTAX_CODE_HINT" dynamicDataTexts={[versionCode]} />
              </FieldHint>
            )}
          </Grid>
        </Grid>
      </AutomobileFormSection>
      {showUsageFields ? (
        <AutomobileFormSection title="VEHICLE_USAGE_AND_IDENTIFICATION_SECTION">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DateFormField
                name={'item.registrationDate'}
                label={'REGISTRATION_DATE'}
                fieldVariant={FieldVariant.form}
                maxDate={new Date()}
                disableFuture
                disabled={isFieldDisabled?.('registrationDate')}
                highlightLabel={highlightEditableFields && !isFieldDisabled?.('registrationDate')}
              />
              <PreviousValueHint previousValue={previousValues?.registrationDate} />
              {registrationDateHint && <FieldHint>{registrationDateHint}</FieldHint>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormField
                name={'item.odometer'}
                label={'ODOMETER'}
                valueUnit={'KM'}
                fieldVariant={FieldVariant.form}
                allowDecimals={false}
                markAsOptional
                disabled={isFieldDisabled?.('odometer')}
                highlightLabel={highlightEditableFields && !isFieldDisabled?.('odometer')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {plateNumberField}
            </Grid>
            <Grid item xs={12} sm={6}>
              {identificationNumberField}
            </Grid>
          </Grid>
        </AutomobileFormSection>
      ) : (
        <AutomobileFormSection title="VEHICLE_IDENTIFICATION_SECTION">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {plateNumberField}
            </Grid>
            <Grid item xs={12} sm={6}>
              {identificationNumberField}
            </Grid>
          </Grid>
        </AutomobileFormSection>
      )}
    </Box>
  );
};
